import { ErrorCode, useDropzone } from 'react-dropzone';
import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { Avatar, Box } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles } from '@material-ui/core/styles';

import { LinkButton } from 'evee-ui-common/components/base';

import PhotoCrop from 'widgets/inputs/PhotoCrop';

import styles from './styles';

const ProfileAvatar = ({
  loading,
  progressSize,
  photoUrl,
  error,
  classes,
  onPhotoChange,
  onUploadError,
  ...rest
}) => {
  const [openCrop, setOpenCrop] = useState(false);
  const [cropPhoto, setCropPhoto] = useState('');

  const profileAvatarClassName = clsx(classes.profileAvatar, error && classes.profileAvatarError);

  const onFileCropped = (croppedFile) => {
    if (croppedFile) {
      onPhotoChange(croppedFile);
    }
    setOpenCrop(false);
  };

  const onDrop = useCallback(
    ([acceptedFile], [rejectedFile]) => {
      if (acceptedFile) {
        const reader = new FileReader();
        reader.addEventListener('load', () => {
          setCropPhoto(reader.result);
          setOpenCrop(true);
        });
        reader.readAsDataURL(acceptedFile);
        return;
      }

      if (rejectedFile) {
        const errorMessage =
          rejectedFile.errors[0].code === ErrorCode.FileInvalidType
            ? 'File type must be image.'
            : rejectedFile.errors[0].message;

        onUploadError(`Can't upload ${rejectedFile.file.name}. ${errorMessage}`);
      }
    },
    [onUploadError],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    noDrag: true,
    accept: 'image/*',
  });

  return (
    <Box display="flex" flexDirection="column" alignItems="center" {...rest}>
      <Box position="relative">
        <Avatar src={photoUrl} className={profileAvatarClassName} />
        {loading && <CircularProgress size={progressSize} className={classes.spinner} />}
      </Box>

      <LinkButton disabled={loading} text="Upload photo" {...getRootProps()} />

      <PhotoCrop circularCrop open={openCrop} src={cropPhoto} onComplete={onFileCropped} />

      <input {...getInputProps()} />
    </Box>
  );
};

ProfileAvatar.propTypes = {
  loading: PropTypes.bool,
  progressSize: PropTypes.number,
  photoUrl: PropTypes.string,
  error: PropTypes.bool,
  classes: PropTypes.shape({
    profileAvatar: PropTypes.string,
    profileAvatarError: PropTypes.string,
    buttonAvatar: PropTypes.string,
    spinner: PropTypes.string,
  }).isRequired,
  onPhotoChange: PropTypes.func,
  onUploadError: PropTypes.func,
};

ProfileAvatar.defaultProps = {
  loading: false,
  progressSize: 128,
  photoUrl: '',
  error: false,
  onPhotoChange: () => {},
  onUploadError: () => {},
};

export default withStyles(styles)(ProfileAvatar);
